import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  BarChart3, Users, Eye, Clock, LogIn, LogOut, Globe2, FileText,
  Smartphone, Monitor, Tablet, MapPin, Loader2, Filter, TrendingDown, UserPlus,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const PERIODS = [
  { v: 7, label: "7 jours" },
  { v: 30, label: "30 jours" },
  { v: 90, label: "90 jours" },
];

const PROV_COLORS = ["#E05638", "#1877F2", "#25D366", "#0A66C2", "#f0a23a", "#8b5cf6", "#111827", "#94a3b8"];
const DEVICE_META = {
  desktop: { label: "Ordinateur", icon: Monitor, color: "#1877F2" },
  mobile: { label: "Mobile", icon: Smartphone, color: "#E05638" },
  tablet: { label: "Tablette", icon: Tablet, color: "#25D366" },
};

function Kpi({ icon: Icon, label, value, suffix, hint, tone = "slate" }) {
  const tones = {
    slate: "text-slate-900", orange: "text-[#E05638]", blue: "text-[#1877F2]",
    red: "text-rose-600", green: "text-emerald-600",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`kpi-${label}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
        <Icon size={15} className="text-[#E05638]" /> {label}
      </div>
      <div className={`mt-2 text-2xl font-extrabold ${tones[tone]}`}>
        {value}{suffix ? <span className="text-base font-bold text-slate-400"> {suffix}</span> : null}
      </div>
      {hint ? <div className="mt-0.5 text-[11px] text-slate-400">{hint}</div> : null}
    </div>
  );
}

function fmtDuration(sec) {
  if (!sec) return "0s";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function BarList({ title, icon: Icon, rows, nameKey = "path", valueKey = "count", suffixFor, testid }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
      <div className="font-heading font-extrabold text-slate-900 mb-4 flex items-center gap-2">
        <Icon size={18} className="text-[#E05638]" /> {title}
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-sm">Pas encore de données.</div>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-3">
              <div className="w-40 sm:w-52 text-xs font-medium text-slate-700 truncate shrink-0" title={r[nameKey]}>
                {r[nameKey] || "—"}
              </div>
              <div className="flex-1 h-4 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.round(((r[valueKey] || 0) / max) * 100)}%`, background: PROV_COLORS[i % PROV_COLORS.length], minWidth: r[valueKey] ? 6 : 0 }} />
              </div>
              <div className="w-20 text-right text-xs font-bold text-slate-900">
                {r[valueKey]}{suffixFor ? <span className="text-slate-400 font-normal"> {suffixFor(r)}</span> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminAudience() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/audience?days=${days}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  const k = data?.kpis || {};
  const funnel = data?.funnel || [];
  const funnelMax = Math.max(1, ...(funnel.map((f) => f.count) || [1]));

  return (
    <div data-testid="admin-audience-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <BarChart3 className="text-[#E05638]" /> Analyse d'audience
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Provenance, durée, pages d'entrée/sortie, localisation et entonnoir de conversion — pour comprendre votre trafic.
          </p>
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl" data-testid="audience-period">
          {PERIODS.map((p) => (
            <button key={p.v} onClick={() => setDays(p.v)} data-testid={`period-${p.v}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${days === p.v ? "bg-white shadow text-[#E05638]" : "text-slate-600"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center text-slate-400"><Loader2 className="animate-spin mx-auto mb-2" /> Chargement…</div>
      ) : !data ? (
        <div className="py-24 text-center text-slate-400">Statistiques indisponibles.</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi icon={Eye} label="Visites" value={k.visits ?? 0} tone="orange" hint={`sur ${days} j`} />
            <Kpi icon={Users} label="Visiteurs" value={k.unique_visitors ?? 0} tone="blue" hint="uniques" />
            <Kpi icon={Globe2} label="Sessions" value={k.sessions ?? 0} hint="visites groupées" />
            <Kpi icon={Clock} label="Durée moy." value={fmtDuration(k.avg_session_seconds ?? 0)} hint="par session" />
            <Kpi icon={FileText} label="Pages / session" value={k.pages_per_session ?? 0} />
            <Kpi icon={TrendingDown} label="Taux de rebond" value={`${k.bounce_rate ?? 0}%`} tone="red" hint="1 seule page" />
          </div>

          {/* Entonnoir de conversion */}
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="audience-funnel">
            <div className="font-heading font-extrabold text-slate-900 mb-1 flex items-center gap-2">
              <Filter size={18} className="text-[#E05638]" /> Entonnoir de conversion
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Où vos visiteurs décrochent avant de s'inscrire. {data.signups_period ?? 0} inscription(s) réelle(s) sur la période.
            </p>
            <div className="space-y-2.5">
              {funnel.map((f, i) => {
                const pct = Math.round((f.count / funnelMax) * 100);
                const base = funnel[0]?.count || 0;
                const share = base > 0 ? Math.round((f.count / base) * 100) : 0;
                return (
                  <div key={i} className="flex items-center gap-3" data-testid={`funnel-step-${i}`}>
                    <div className="w-48 text-sm font-semibold text-slate-700 shrink-0">{f.step}</div>
                    <div className="flex-1 h-7 rounded-lg bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-lg flex items-center justify-end px-2 text-white text-xs font-bold transition-all"
                        style={{ width: f.count > 0 ? `${Math.max(pct, 6)}%` : "0%", background: PROV_COLORS[i % PROV_COLORS.length] }}>
                        {f.count > 0 ? f.count : ""}
                      </div>
                    </div>
                    <div className="w-16 text-right text-xs font-bold text-slate-500">
                      {i === 0 ? "100%" : `${share}%`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Courbe visites / visiteurs / sessions */}
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="audience-series">
            <div className="font-heading font-extrabold text-slate-900 mb-4">Évolution du trafic</div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.series}>
                <defs>
                  <linearGradient id="gav" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#E05638" stopOpacity={0.3} /><stop offset="95%" stopColor="#E05638" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gau" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1877F2" stopOpacity={0.3} /><stop offset="95%" stopColor="#1877F2" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gas" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#25D366" stopOpacity={0.3} /><stop offset="95%" stopColor="#25D366" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="visits" name="Visites" stroke="#E05638" strokeWidth={2} fill="url(#gav)" />
                <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#25D366" strokeWidth={2} fill="url(#gas)" />
                <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke="#1877F2" strokeWidth={2} fill="url(#gau)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Provenance + Pages */}
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <BarList title="Provenance du trafic" icon={Globe2} rows={data.provenance} nameKey="source" testid="audience-provenance" />
            <BarList title="Pages les plus visitées" icon={FileText} rows={data.top_pages} nameKey="path"
              suffixFor={(r) => r.avg_duration ? `· ${fmtDuration(r.avg_duration)}` : ""} testid="audience-top-pages" />
          </div>

          {/* Entrée / Sortie */}
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <BarList title="Pages d'entrée" icon={LogIn} rows={data.entry_pages} nameKey="path" testid="audience-entry" />
            <BarList title="Pages de sortie" icon={LogOut} rows={data.exit_pages} nameKey="path" testid="audience-exit" />
          </div>

          {/* Formulaires */}
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="audience-forms">
            <div className="font-heading font-extrabold text-slate-900 mb-1 flex items-center gap-2">
              <FileText size={18} className="text-[#E05638]" /> Formulaires — vues &amp; conversions
            </div>
            <p className="text-xs text-slate-500 mb-4">Combien de visiteurs ouvrent un formulaire et combien le valident.</p>
            {(data.forms || []).length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">Aucune vue de formulaire pour l'instant.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-2 pr-4 font-semibold">Formulaire</th>
                      <th className="py-2 px-4 font-semibold text-right">Vues</th>
                      <th className="py-2 px-4 font-semibold text-right">Envois</th>
                      <th className="py-2 pl-4 font-semibold text-right">Taux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.forms.map((f) => (
                      <tr key={f.form} className="border-b border-slate-50" data-testid={`form-row-${f.form}`}>
                        <td className="py-2 pr-4 font-medium text-slate-800">{f.label}</td>
                        <td className="py-2 px-4 text-right">{f.views}</td>
                        <td className="py-2 px-4 text-right">{f.submits}</td>
                        <td className="py-2 pl-4 text-right font-bold" style={{ color: f.rate >= 20 ? "#059669" : f.rate >= 5 ? "#E05638" : "#e11d48" }}>{f.rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Localisation + Appareils */}
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="audience-geo">
              <div className="font-heading font-extrabold text-slate-900 mb-4 flex items-center gap-2">
                <MapPin size={18} className="text-[#E05638]" /> Localisation des visiteurs
              </div>
              <div className="flex gap-2 mb-4 text-xs">
                <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">France {data.geo?.split?.france ?? 0}</span>
                <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold">Étranger {data.geo?.split?.etranger ?? 0}</span>
                <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 font-semibold">Inconnu {data.geo?.split?.inconnu ?? 0}</span>
              </div>
              {(data.geo?.cities || []).length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">Localisation en cours de collecte (quelques visites nécessaires).</div>
              ) : (
                <ul className="space-y-2">
                  {data.geo.cities.map((c, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{c.name}</span>
                      <span className="font-bold text-slate-900">{c.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="audience-devices">
              <div className="font-heading font-extrabold text-slate-900 mb-4 flex items-center gap-2">
                <Smartphone size={18} className="text-[#E05638]" /> Appareils
              </div>
              {(data.devices || []).length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">Pas encore de données.</div>
              ) : (
                <ul className="space-y-3">
                  {data.devices.map((d, i) => {
                    const meta = DEVICE_META[d.device] || { label: d.device, icon: Monitor, color: "#94a3b8" };
                    const Icon = meta.icon;
                    const total = data.devices.reduce((s, x) => s + x.count, 0) || 1;
                    const pct = Math.round((d.count / total) * 100);
                    return (
                      <li key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="flex items-center gap-2 text-slate-700"><Icon size={15} style={{ color: meta.color }} /> {meta.label}</span>
                          <span className="font-bold text-slate-900">{d.count} <span className="text-slate-400 font-normal">· {pct}%</span></span>
                        </div>
                        <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: meta.color }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
